import { lazy, Suspense } from 'react'
import type { RouteObject } from 'react-router-dom'
import { Navigate, useRouteError, isRouteErrorResponse, Link } from 'react-router-dom'
import { ProtectedRoute } from './ProtectedRoute'
import { AuthRoute } from './AuthRoute'
import { ForcePasswordRoute } from './ForcePasswordRoute'
import { RoleLanding } from './RoleLanding'
import { AppLayout } from '@/components/layout/AppLayout'
import { ErrorBoundary } from '@/components/layout/ErrorBoundary'
import { PageSpinner } from '@/components/ui/loading'
import { makeProtectedRoute, type RouteHandle } from './makeProtectedRoute'
import { PolicyIdRedirect } from './pages/PolicyIdRedirect'
import { NotFoundPage } from './pages/NotFoundPage'

function RouterErrorPage() {
  const error = useRouteError()
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundPage />
  }
  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-background">
      <div className="text-center max-w-sm">
        <p className="text-6xl font-bold text-destructive mb-4">!</p>
        <h1 className="text-xl font-semibold text-foreground mb-2">เกิดข้อผิดพลาด</h1>
        <p className="text-sm text-foreground-muted mb-6">
          {isRouteErrorResponse(error) ? error.statusText : 'An unexpected error occurred'}
        </p>
        <Link
          to="/dashboard"
          className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2 text-sm font-medium text-white hover:bg-primary-hover transition-colors"
        >
          กลับหน้าหลัก
        </Link>
      </div>
    </div>
  )
}

// Lazy-loaded page components
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const ChangePasswordPage = lazy(() => import('@/pages/ChangePasswordPage'))
const AccessDeniedPage = lazy(() => import('@/pages/AccessDeniedPage'))

// User management pages (moved from /admin/users to /users)
const UsersListPage = lazy(() => import('@/pages/admin/UsersListPage'))
const UserCreatePage = lazy(() => import('@/pages/admin/UserCreatePage'))
const UserDetailPage = lazy(() => import('@/pages/admin/UserDetailPage'))
const TeamsListPage = lazy(() => import('@/pages/admin/TeamsListPage'))
const TeamDetailPage = lazy(() => import('@/pages/admin/TeamDetailPage'))
const AdvisorRegistrationsListPage = lazy(() => import('@/pages/admin/AdvisorRegistrationsListPage'))
const AdvisorRegistrationCreatePage = lazy(() => import('@/pages/admin/AdvisorRegistrationCreatePage'))
const AdvisorRegistrationDetailPage = lazy(() => import('@/pages/admin/AdvisorRegistrationDetailPage'))

// Advisor portal pages
const AdvisorDashboardPage = lazy(() => import('@/pages/advisor/DashboardPage'))

// Advisor Proposal pages
const ProposalCreatePage = lazy(() => import('@/pages/advisor/proposals/ProposalCreatePage'))

// Case pages (resource-based)
const CasesRouter = lazy(() => import('@/pages/cases/CasesRouter'))
const CaseDetailRouter = lazy(() => import('@/pages/cases/CaseDetailRouter'))

// Policy pages removed — consolidated into /cases?filter=policy
const ClientInfoPage = lazy(() => import('@/pages/cases/ClientInfoPage'))

// Advisor Commission pages
const CommissionPage = lazy(() => import('@/pages/advisor/commission/CommissionPage'))
const CommissionHistoryPage = lazy(() => import('@/pages/advisor/commission/CommissionHistoryPage'))

// Advisor Client pages
const ClientListPage = lazy(() => import('@/pages/advisor/clients/ClientListPage'))
const ClientDetailPage = lazy(() => import('@/pages/advisor/clients/ClientDetailPage'))

// Team Leader pages
const MyTeamPage = lazy(() => import('@/pages/team/MyTeamPage'))

// Admin back-office pages (Badge Model permissions)
const CommissionEntryPage = lazy(() => import('@/pages/admin/CommissionEntryPage'))
const ProductManagementPage = lazy(() => import('@/pages/admin/ProductManagementPage'))

// Set password page (public, token-based)
const SetPasswordPage = lazy(() => import('@/pages/SetPasswordPage'))

// Invite accept page (public, token-based — invited advisors create their account)
const InviteAcceptPage = lazy(() => import('@/pages/InviteAcceptPage'))

// Wrapper for lazy-loaded components rendered BEFORE AppLayout exists
// (login / public / auth pages, AppLayout chunk itself) — full-screen fallback.
function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSpinner fullScreen />}>{children}</Suspense>
    </ErrorBoundary>
  )
}

/**
 * Route definitions for the application
 * Updated: Resource-based routing (no more /advisor prefix for main routes)
 */
export const routes: RouteObject[] = [
  // Root → role-based landing (advisor → /dashboard, WRISE/admin → /cases)
  {
    path: '/',
    element: <RoleLanding />,
  },

  // Auth routes (redirect away if already logged in)
  {
    path: '/login',
    element: (
      <AuthRoute>
        <LazyPage>
          <LoginPage />
        </LazyPage>
      </AuthRoute>
    ),
  },

  // Set password page (public, token-based — for advisor invitation flow)
  {
    path: '/set-password',
    element: (
      <LazyPage>
        <SetPasswordPage />
      </LazyPage>
    ),
  },

  // Invite accept page (public, token-based)
  {
    path: '/invite/accept',
    element: (
      <LazyPage>
        <InviteAcceptPage />
      </LazyPage>
    ),
  },


  // Force password change route (requires auth, but allows forcePasswordChange)
  {
    path: '/change-password',
    element: (
      <ForcePasswordRoute>
        <LazyPage>
          <ChangePasswordPage />
        </LazyPage>
      </ForcePasswordRoute>
    ),
    handle: {
      title: 'Change Password',
      breadcrumb: 'Change Password',
    } satisfies RouteHandle,
  },

  // Access denied page (for permission errors)
  {
    path: '/access-denied',
    element: (
      <ProtectedRoute>
        <LazyPage>
          <AccessDeniedPage />
        </LazyPage>
      </ProtectedRoute>
    ),
    handle: {
      title: 'Access Denied',
      breadcrumb: 'Access Denied',
    } satisfies RouteHandle,
  },

  // Protected routes with AppLayout
  {
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouterErrorPage />,
    children: [
      // Admin user list (Spec A T2, screen 2) — DI pattern, mock API
      makeProtectedRoute({
        path: '/admin/users',
        lazy: UsersListPage,
        perm: { permission: 'users:read' },
        handle: { title: 'Users', breadcrumb: 'Users' },
      }),
      // Admin user create (Spec A T3, screen 3) — DI pattern, mock API
      makeProtectedRoute({
        path: '/admin/users/create',
        lazy: UserCreatePage,
        perm: { permission: 'users:create' },
        handle: { title: 'Create User', breadcrumb: 'Create' },
      }),
      // Admin user detail/edit (Spec A T1, screen 1) — DI pattern, mock API
      makeProtectedRoute({
        path: '/admin/users/:id/edit',
        lazy: UserDetailPage,
        perm: { permission: 'users:update' },
        handle: { title: 'Edit User', breadcrumb: 'Edit' },
      }),

      // Admin teams (web#38, web#45). Static path first, then /:id.
      makeProtectedRoute({
        path: '/admin/teams',
        lazy: TeamsListPage,
        perm: { permission: 'teams:manage' },
        handle: { title: 'Teams', breadcrumb: 'Teams' },
      }),
      makeProtectedRoute({
        path: '/admin/teams/:id',
        lazy: TeamDetailPage,
        perm: { permission: 'teams:manage' },
        handle: { title: 'Team', breadcrumb: 'Team' },
      }),
      makeProtectedRoute({
        path: '/admin/teams/:id/company',
        lazy: TeamDetailPage,
        perm: { permission: 'teams:manage' },
        handle: { title: 'Team', breadcrumb: 'Team' },
      }),

      // Admin advisor-registration list (Spec A T4, screen 4) — DI pattern, mock API
      makeProtectedRoute({
        path: '/admin/advisor-registrations',
        lazy: AdvisorRegistrationsListPage,
        perm: { anyPermissions: ['advisor_registrations:manage', 'advisor_registrations:approve'] },
        handle: { title: 'Advisors', breadcrumb: 'Advisors' },
      }),
      // Admin-initiated advisor registration — create + auto-approve + invite
      // (Spec A T5, screen 5). /new MUST precede /:id so it isn't captured as an id.
      makeProtectedRoute({
        path: '/admin/advisor-registrations/new',
        lazy: AdvisorRegistrationCreatePage,
        perm: { anyPermissions: ['advisor_registrations:manage', 'advisor_registrations:approve'] },
        handle: { title: 'Create advisor', breadcrumb: 'Create advisor' },
      }),
      // Admin advisor-registration detail + reverse actions (Spec A T5, screen 5)
      makeProtectedRoute({
        path: '/admin/advisor-registrations/:id',
        lazy: AdvisorRegistrationDetailPage,
        perm: { anyPermissions: ['advisor_registrations:manage', 'advisor_registrations:approve'] },
        handle: { title: 'Advisor Registration', breadcrumb: 'Advisor Registration' },
      }),

      // ================================================================
      // Resource-based routes (new canonical paths)
      // ================================================================

      // Dashboard
      makeProtectedRoute({
        path: '/dashboard',
        lazy: AdvisorDashboardPage,
        perm: { permission: 'advisor:access' },
        handle: { title: 'Dashboard', breadcrumb: 'Dashboard' },
      }),

      // Cases
      makeProtectedRoute({
        path: '/cases',
        lazy: CasesRouter,
        perm: { anyPermissions: ['advisor:access', 'client_applications:wrise_comment'] },
        handle: { title: 'Cases', breadcrumb: 'Cases' },
      }),
      makeProtectedRoute({
        path: '/cases/new',
        lazy: ProposalCreatePage,
        perm: { permission: 'advisor:access' },
        handle: { title: 'Create Proposal', breadcrumb: 'Create' },
      }),
      makeProtectedRoute({
        path: '/cases/:id',
        lazy: CaseDetailRouter,
        perm: { anyPermissions: ['advisor:access', 'client_applications:wrise_comment'] },
        handle: { title: 'Case Detail', breadcrumb: 'Case' },
      }),

      // Policies → redirect to /cases?filter=policy
      {
        path: '/policies',
        element: <Navigate to="/cases?filter=policy" replace />,
      },
      {
        path: '/policies/calendar',
        element: <Navigate to="/cases?filter=policy" replace />,
      },
      {
        path: '/policies/:id',
        element: <PolicyIdRedirect />,
      },

      // Client Info (Stage 2)
      makeProtectedRoute({
        path: '/cases/:id/client-info',
        lazy: ClientInfoPage,
        perm: { anyPermissions: ['advisor:access', 'client_applications:wrise_comment'] },
        handle: { title: 'Client Information', breadcrumb: 'Client Info' },
      }),

      // Commission
      makeProtectedRoute({
        path: '/commission',
        lazy: CommissionPage,
        perm: { permission: 'advisor:access' },
        handle: { title: 'Commission', breadcrumb: 'Commission' },
      }),
      makeProtectedRoute({
        path: '/commission/history',
        lazy: CommissionHistoryPage,
        perm: { permission: 'advisor:access' },
        handle: { title: 'Commission History', breadcrumb: 'History' },
      }),

      // Clients
      makeProtectedRoute({
        path: '/clients',
        lazy: ClientListPage,
        perm: { permission: 'advisor:access' },
        handle: { title: 'Clients', breadcrumb: 'Clients' },
      }),
      makeProtectedRoute({
        path: '/clients/:id',
        lazy: ClientDetailPage,
        perm: { permission: 'advisor:access' },
        handle: { title: 'Client Detail', breadcrumb: 'Detail' },
      }),

      // Team Leader routes - Protected with team:view permission (Badge Model)
      // Team Leader = Advisor + Team management (dual role)
      makeProtectedRoute({
        path: '/team',
        lazy: MyTeamPage,
        perm: { permission: 'team:view' },
        handle: { title: 'My Team', breadcrumb: 'Team' },
      }),
      makeProtectedRoute({
        path: '/team/cases',
        lazy: MyTeamPage,
        perm: { permission: 'team:view' },
        handle: { title: 'Team cases', breadcrumb: 'Cases' },
      }),
      makeProtectedRoute({
        path: '/team/company',
        lazy: MyTeamPage,
        perm: { permission: 'team:view' },
        handle: { title: 'Team company', breadcrumb: 'Company' },
      }),

      // Admin Back-Office Routes (Badge Model permissions)
      makeProtectedRoute({
        path: '/commission-entry',
        lazy: CommissionEntryPage,
        perm: { permission: 'commission:entry' },
        handle: { title: 'Commission Entry', breadcrumb: 'Commission Entry' },
      }),
      makeProtectedRoute({
        path: '/product-management',
        lazy: ProductManagementPage,
        perm: { permission: 'product:manage' },
        handle: { title: 'Product Management', breadcrumb: 'Product Management' },
      }),
    ],
  },

  // Legacy /advisor-dev redirect to "/" (RoleLanding routes per role)
  {
    path: '/advisor-dev',
    element: <Navigate to="/" replace />,
  },
  {
    path: '/advisor-dev/*',
    element: <Navigate to="/" replace />,
  },

  // Legacy route redirects (from /admin/* to /*)
  {
    path: '/admin',
    element: <Navigate to="/" replace />,
  },
  {
    path: '/admin/roles',
    element: <Navigate to="/" replace />,
  },

  // Legacy /advisor/cases and /advisor/proposals redirects
  {
    path: '/advisor/cases',
    element: <Navigate to="/cases" replace />,
  },
  {
    path: '/advisor/cases/:id',
    element: <Navigate to="/cases/:id" replace />,
  },
  {
    path: '/advisor/proposals',
    element: <Navigate to="/cases" replace />,
  },
  {
    path: '/advisor/proposals/create',
    element: <Navigate to="/cases/new" replace />,
  },
  {
    path: '/advisor/policies',
    element: <Navigate to="/cases?filter=policy" replace />,
  },
  {
    path: '/advisor/policies/:id',
    element: <Navigate to="/cases/:id" replace />,
  },

  // Catch-all: 404 for any unmatched route
  {
    path: '*',
    element: <NotFoundPage />,
  },
]
